import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import careersService from '../services/careers.service';
import type { PublicJDItem, ParsedJDData } from '../types/careers';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['.pdf', '.doc', '.docx'];
const ACCEPTED_MIME = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function CareersApplyPage() {
  const { workspaceCode, jdId } = useParams<{ workspaceCode: string; jdId: string }>();
  const navigate = useNavigate();

  // JD data
  const [jd, setJd] = useState<PublicJDItem | null>(null);
  const [jdLoading, setJdLoading] = useState(true);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Validation errors
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch JD for summary
  useEffect(() => {
    if (!workspaceCode || !jdId) return;
    const fetchJD = async () => {
      try {
        const result = await careersService.getCareerJD(workspaceCode, jdId);
        setJd(result);
      } catch {
        // Non-fatal — just don't show summary
      } finally {
        setJdLoading(false);
      }
    };
    fetchJD();
  }, [workspaceCode, jdId]);

  const parsed = jd?.parsed_data as ParsedJDData | null;
  const jobTitle = parsed?.title || jd?.name || 'this position';
  const companyName = parsed?.company_info?.name;

  function validateFile(file: File): string | null {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File is too large. Maximum size is ${MAX_FILE_SIZE_MB} MB.`;
    }
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ACCEPTED_TYPES.includes(ext) && !ACCEPTED_MIME.includes(file.type)) {
      return 'Unsupported file type. Please upload a PDF, DOC, or DOCX file.';
    }
    return null;
  }

  function handleFileSelect(file: File) {
    const err = validateFile(file);
    if (err) {
      setFieldErrors((prev) => ({ ...prev, cv_file: err }));
      return;
    }
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next.cv_file;
      return next;
    });
    setCvFile(file);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }

  function validate(): boolean {
    const errors: Record<string, string> = {};
    if (!name.trim()) errors.name = 'Full name is required.';
    if (!email.trim()) errors.email = 'Email address is required.';
    else if (!isValidEmail(email)) errors.email = 'Please enter a valid email address.';
    if (cvFile) {
      const err = validateFile(cvFile);
      if (err) errors.cv_file = err;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await careersService.submitApplication(workspaceCode!, jdId!, {
        applicant_name: name.trim(),
        applicant_email: email.trim(),
        applicant_phone: phone.trim() || undefined,
        cv_file: cvFile || undefined,
      });
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header
          companyName={companyName}
          onBack={() => navigate(`/${workspaceCode}/jds/${jdId}`)}
        />
        <main className="max-w-2xl mx-auto px-4 py-16 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
          <h2 className="mt-5 text-2xl font-bold text-gray-900">Application Submitted!</h2>
          <p className="mt-3 text-gray-500">
            Thank you, {name}! We've received your application and will be in touch.
          </p>
          <button
            onClick={() => navigate(`/${workspaceCode}`)}
            className="mt-6 inline-flex items-center gap-1.5 text-sm text-orange-600 hover:text-orange-700 font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to all positions
          </button>
        </main>
        <footer className="text-center text-xs text-gray-400 py-6">
          Powered by AaaS
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header
        companyName={companyName}
        onBack={() => navigate(`/${workspaceCode}/jds/${jdId}`)}
      />

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-5">
        {/* Job summary card */}
        {!jdLoading && jd && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-400 mb-0.5">Applying for</p>
            <p className="text-base font-semibold text-gray-900">{jobTitle}</p>
            {companyName && (
              <p className="text-sm text-gray-500 mt-0.5">{companyName}</p>
            )}
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-5">Apply for this position</h1>

          {submitError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-sm text-red-700">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{submitError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Doe"
                className={`w-full rounded-lg border px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-100 transition ${
                  fieldErrors.name ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-orange-400'
                }`}
              />
              {fieldErrors.name && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className={`w-full rounded-lg border px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-100 transition ${
                  fieldErrors.email ? 'border-red-400 focus:border-red-400' : 'border-gray-200 focus:border-orange-400'
                }`}
              />
              {fieldErrors.email && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number <span className="text-gray-400 font-normal text-xs">(optional)</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 000 0000"
                className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100 transition"
              />
            </div>

            {/* CV upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Upload CV <span className="text-gray-400 font-normal text-xs">(optional)</span>
              </label>

              {cvFile ? (
                <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <Upload className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-700 truncate flex-1">{cvFile.name}</span>
                  <button
                    type="button"
                    onClick={() => setCvFile(null)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                    aria-label="Remove file"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`rounded-xl border-2 border-dashed py-10 px-6 text-center cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/30'
                  }`}
                >
                  <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 font-medium">
                    Drop your CV here or{' '}
                    <span className="text-orange-600">click to upload</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PDF, DOCX, DOC — max {MAX_FILE_SIZE_MB} MB
                  </p>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES.join(',')}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFileSelect(file);
                  e.target.value = '';
                }}
              />

              {fieldErrors.cv_file && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.cv_file}</p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg text-base transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </form>
        </div>
      </main>

      <footer className="text-center text-xs text-gray-400 py-6">
        Powered by AaaS
      </footer>
    </div>
  );
}

interface HeaderProps {
  companyName?: string;
  onBack: () => void;
}

function Header({ companyName, onBack }: HeaderProps) {
  return (
    <header className="bg-white border-b border-gray-200 py-4">
      <div className="max-w-2xl mx-auto px-4 flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to job posting
        </button>
        {companyName && (
          <span className="text-sm font-semibold text-gray-700">{companyName}</span>
        )}
      </div>
    </header>
  );
}
